import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auditService } from '../../services/auditService';
import { downloadFile, fmtQty, safeFileName, todayStamp, XLSX_TYPE } from './ui';
import { auditDateRange, buildVarianceWorkbook } from './varianceReport';
import {
  PULL_FIELDS,
  buildLookup,
  combineLookups,
  coverage,
  guessHeaderRow,
  guessMapping,
  readSourceFile,
  recallMapping,
  rememberMapping,
} from './reportSources';
import { startLoading } from '../../utils/loader';

const SOURCES = [
  {
    kind: 'book',
    title: 'Book stock file',
    hint: 'The client’s book stock file (the one uploaded on the portal, or a fuller version with more columns).',
  },
  {
    kind: 'amd',
    title: 'AMD file (article master)',
    hint: 'The article master with category, class, size, colour, brand…',
  },
];

const colLabel = (headers, rows, headerRow, index) => {
  const header = String(headers[index] ?? '').trim() || `Column ${index + 1}`;
  const sample = rows.slice(headerRow + 1, headerRow + 40).map((r) => String(r?.[index] ?? '').trim()).find(Boolean);
  return sample ? `${header} (e.g. ${sample.length > 24 ? `${sample.slice(0, 24)}…` : sample})` : header;
};

/** One optional file (book stock or AMD) and which of its columns go into the report. */
function SourceCard({ source, state, setState, items }) {
  const { kind, title, hint } = source;
  const headers = useMemo(() => (state?.rows ? state.rows[state.headerRow] || [] : []), [state]);

  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setState({ reading: true, file });
    try {
      const preview = await readSourceFile(file);
      const sheetIndex = 0;
      const rows = await preview.readAll(sheetIndex);
      const headerRow = guessHeaderRow(rows);
      const head = rows[headerRow] || [];
      setState({ file, preview, sheetIndex, rows, headerRow, mapping: recallMapping(kind, head, guessMapping(head)) });
    } catch (err) {
      setState({ file, error: err.message });
    }
  };

  const changeSheet = async (sheetIndex) => {
    setState({ ...state, reading: true });
    try {
      const rows = await state.preview.readAll(sheetIndex);
      const headerRow = guessHeaderRow(rows);
      const head = rows[headerRow] || [];
      setState({ ...state, reading: false, sheetIndex, rows, headerRow, mapping: recallMapping(kind, head, guessMapping(head)) });
    } catch (err) {
      setState({ ...state, reading: false, error: err.message });
    }
  };

  const changeHeader = (headerRow) => {
    const head = state.rows[headerRow] || [];
    setState({ ...state, headerRow, mapping: recallMapping(kind, head, guessMapping(head)) });
  };

  const setCol = (key, value) => setState({ ...state, mapping: { ...state.mapping, [key]: value } });

  const options = headers.map((_, index) => (
    <option key={index} value={String(index)}>
      {colLabel(headers, state.rows, state.headerRow, index)}
    </option>
  ));
  const pulled = state?.mapping ? PULL_FIELDS.filter((f) => state.mapping[f.key] !== '').length : 0;
  const keyMissing = state?.mapping && state.mapping.ean === '' && state.mapping.article === '';

  return (
    <article className={`rl-source${state?.file ? ' is-on' : ''}`}>
      <header>
        <div>
          <h4>{title}</h4>
          <p>{hint}</p>
        </div>
        <div className="rl-source__actions">
          <label className="rl-btn rl-btn--ghost">
            {state?.reading ? 'Reading…' : state?.file ? 'Change file' : 'Choose file'}
            <input type="file" accept=".xlsx,.xlsb,.csv,.txt,.tsv" hidden onChange={choose} disabled={state?.reading} />
          </label>
          {state?.file ? (
            <button type="button" className="rl-link" onClick={() => setState(null)}>
              Don’t use
            </button>
          ) : null}
        </div>
      </header>

      {state?.error ? <p className="rl-error">{state.error}</p> : null}

      {state?.rows ? (
        <>
          <div className="rl-source__file">
            <strong>{state.file.name}</strong>
            <span>
              {fmtQty(state.lookup?.rows || 0)} items read · matches{' '}
              <b className={state.covered ? 'rl-good' : 'rl-bad'}>{fmtQty(state.covered || 0)}</b> of {fmtQty(items.length)} report items
            </span>
          </div>
          <div className="rl-source__grid">
            {state.preview.sheets.length > 1 ? (
              <label>
                Sheet
                <select value={state.sheetIndex} onChange={(e) => changeSheet(Number(e.target.value))}>
                  {state.preview.sheets.map((s, i) => (
                    <option key={s.name + i} value={i}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Header row
              <select value={state.headerRow} onChange={(e) => changeHeader(Number(e.target.value))}>
                {state.rows.slice(0, 20).map((row, i) => (
                  <option key={i} value={i}>
                    Row {i + 1}: {(row || []).filter((c) => String(c).trim()).slice(0, 4).join(' | ').slice(0, 60) || '(blank)'}
                  </option>
                ))}
              </select>
            </label>
            <label className="rl-key">
              Match items by — EAN column
              <select value={state.mapping.ean} onChange={(e) => setCol('ean', e.target.value)}>
                <option value="">— none —</option>
                {options}
              </select>
            </label>
          </div>
          {keyMissing ? <p className="rl-error">Pick the EAN column (or the Article column below) so items can be matched.</p> : null}

          <p className="rl-source__sub">
            Columns to pull into the report <span>({pulled} chosen — blank fields only; portal values stay)</span>
          </p>
          <div className="rl-source__fields">
            {PULL_FIELDS.map((field) => (
              <label key={field.key} className={state.mapping[field.key] !== '' ? 'is-set' : ''}>
                <span>{field.label}</span>
                <select value={state.mapping[field.key]} onChange={(e) => setCol(field.key, e.target.value)}>
                  <option value="">— don’t pull —</option>
                  {options}
                </select>
              </label>
            ))}
          </div>
        </>
      ) : null}
    </article>
  );
}

/**
 * "Generate report" for one schedule: checks what the portal has, lets the
 * user pull extra columns from the book stock / AMD files, then downloads
 * the styled variance report.
 */
function GenerateReportDialog({ audit, onClose }) {
  const [portal, setPortal] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [sources, setSources] = useState({ book: null, amd: null });
  const [useFallback, setUseFallback] = useState(false);
  const [percent, setPercent] = useState(70);
  const [sheets, setSheets] = useState({ differences: true, reco: true, scans: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([auditService.getComparison(audit.id, 'book'), auditService.getReco(audit.id)])
      .then(([comparison, reco]) => alive && setPortal({ comparison, reco }))
      .catch((err) => alive && setLoadError(err.message));
    return () => {
      alive = false;
    };
  }, [audit.id]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const items = useMemo(() => portal?.comparison?.items || [], [portal]);

  // Lookups and coverage, rebuilt when a file or its columns change.
  const withLookups = useMemo(() => {
    const out = {};
    SOURCES.forEach(({ kind }) => {
      const state = sources[kind];
      if (!state?.rows || !state.mapping) {
        out[kind] = state;
        return;
      }
      const lookup = buildLookup(state.rows, state.headerRow, state.mapping);
      out[kind] = { ...state, lookup, covered: coverage(lookup, items) };
    });
    return out;
  }, [sources, items]);

  const stats = useMemo(() => {
    const recoEans = new Set((portal?.reco?.entries || []).map((e) => String(e.ean)));
    return {
      items: items.length,
      inBook: items.filter((i) => i.in_book).length,
      variance: items.filter((i) => i.variance !== 0).length,
      reco: items.filter((i) => recoEans.has(String(i.ean))).length,
      noCost: items.filter((i) => i.in_book && (i.cost === null || i.cost === undefined)).length,
      adjustments: (portal?.comparison?.adjustment_categories || []).length,
    };
  }, [items, portal]);

  const generate = async () => {
    setBusy(true);
    setError('');
    setDone('');
    const task = startLoading('Preparing the variance report…');
    try {
      const scans = sheets.scans ? await auditService.getComparison(audit.id, 'scans') : null;
      task.update(null, 'Building the Excel file');
      const used = SOURCES.filter(({ kind }) => withLookups[kind]?.lookup);
      used.forEach(({ kind }) => {
        const s = withLookups[kind];
        rememberMapping(kind, s.rows[s.headerRow] || [], s.mapping);
      });
      const extras = combineLookups(used.map(({ kind }) => withLookups[kind].lookup));
      const { bytes } = await buildVarianceWorkbook(
        { audit, comparison: portal.comparison, reco: portal.reco, scans },
        {
          extras,
          extraFrom: used.length ? used.map(({ kind, title }) => `${title}: ${withLookups[kind].file.name}`).join(' · ') : 'Portal only',
          costFallback: useFallback && Number(percent) > 0 ? { percent: Number(percent) } : null,
          sheets,
        },
      );
      const name = `${safeFileName([audit.schedule_no, audit.outlet_name || audit.name].filter(Boolean).join(' - '))} - Variance report ${todayStamp()}.xlsx`;
      downloadFile(name, bytes, XLSX_TYPE);
      setDone(name);
    } catch (err) {
      setError(`Report not made: ${err.message}`);
    } finally {
      task.done();
      setBusy(false);
    }
  };

  return (
    <div className="rl-modal" role="dialog" aria-modal="true" aria-labelledby="rl-modal-title">
      <div className="rl-modal__backdrop" onClick={() => !busy && onClose()} />
      <div className="rl-modal__panel">
        <header className="rl-modal__head">
          <div>
            <p className="rl-eyebrow">Variance report</p>
            <h3 id="rl-modal-title">{audit.schedule_no || audit.name}</h3>
            <p className="rl-muted">
              {[audit.organization_name, audit.outlet_code && `${audit.outlet_code}`, audit.outlet_name].filter(Boolean).join(' · ')}
              {auditDateRange(audit) ? ` · ${auditDateRange(audit)}` : ''}
            </p>
          </div>
          <button type="button" className="rl-close" onClick={onClose} disabled={busy} aria-label="Close">
            ×
          </button>
        </header>

        <div className="rl-modal__body">
          {loadError ? <p className="rl-error">{loadError}</p> : null}
          {!portal && !loadError ? <p className="rl-muted">Checking the schedule’s data…</p> : null}

          {portal ? (
            <>
              <section>
                <h4 className="rl-step">
                  <span>1</span> From the portal
                </h4>
                <div className="rl-checks">
                  <div className={portal.comparison?.book ? 'ok' : 'warn'}>
                    <strong>Book stock</strong>
                    <span>
                      {portal.comparison?.book
                        ? `${portal.comparison.book.original_filename} · ${fmtQty(stats.inBook)} items`
                        : 'Not uploaded — every item will show book 0'}
                    </span>
                  </div>
                  <div className="ok">
                    <strong>Counted</strong>
                    <span>
                      Scans{stats.adjustments ? ` + ${stats.adjustments} manual adjustment tab${stats.adjustments === 1 ? '' : 's'}` : ''} ·{' '}
                      {fmtQty(stats.items)} items
                    </span>
                  </div>
                  <div className={stats.variance && !stats.reco ? 'warn' : 'ok'}>
                    <strong>Reco</strong>
                    <span>
                      {fmtQty(stats.reco)} of {fmtQty(stats.variance)} variance items reconciled ·{' '}
                      <Link to={`/audits/${audit.id}/portal`}>enter on the portal (Reco tab)</Link>
                    </span>
                  </div>
                  <div className={stats.noCost ? 'warn' : 'ok'}>
                    <strong>Cost (CP)</strong>
                    <span>{stats.noCost ? `${fmtQty(stats.noCost)} book items have no CP in the book stock` : 'From the book stock'}</span>
                  </div>
                </div>
              </section>

              <section>
                <h4 className="rl-step">
                  <span>2</span> Extra columns from files <em>(optional)</em>
                </h4>
                <p className="rl-muted">
                  Category, sub-category, class, size, colour, brand… are not stored on the portal. Pick the files and the columns
                  to bring in; items are matched by EAN (or article). Files are read on this computer only.
                </p>
                <div className="rl-sources">
                  {SOURCES.map((source) => (
                    <SourceCard
                      key={source.kind}
                      source={source}
                      items={items}
                      state={withLookups[source.kind]}
                      setState={(next) =>
                        setSources((prev) => {
                          if (!next) return { ...prev, [source.kind]: null };
                          const { lookup, covered, ...plain } = next;
                          return { ...prev, [source.kind]: plain };
                        })
                      }
                    />
                  ))}
                </div>
              </section>

              <section>
                <h4 className="rl-step">
                  <span>3</span> Options
                </h4>
                <div className="rl-options">
                  <label className="rl-check">
                    <input type="checkbox" checked={useFallback} onChange={(e) => setUseFallback(e.target.checked)} />
                    <span>
                      When an item has no CP, use
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={percent}
                        onChange={(e) => setPercent(e.target.value)}
                        disabled={!useFallback}
                        aria-label="Percent of MRP"
                      />
                      % of MRP (rounded up)
                    </span>
                  </label>
                  <div className="rl-sheets">
                    <span>Sheets:</span>
                    <label className="rl-pill is-fixed">Summary</label>
                    <label className="rl-pill is-fixed">All items</label>
                    {[
                      ['differences', 'Differences'],
                      ['reco', 'Reco'],
                      ['scans', 'Valid / invalid scans'],
                    ].map(([key, label]) => (
                      <label key={key} className={`rl-pill${sheets[key] ? ' is-on' : ''}`}>
                        <input type="checkbox" checked={sheets[key]} onChange={(e) => setSheets({ ...sheets, [key]: e.target.checked })} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {error ? <p className="rl-error">{error}</p> : null}
          {done ? <p className="rl-done">Downloaded “{done}”.</p> : null}
        </div>

        <footer className="rl-modal__foot">
          <button type="button" className="rl-btn rl-btn--ghost" onClick={onClose} disabled={busy}>
            Close
          </button>
          <button type="button" className="rl-btn rl-btn--primary" onClick={generate} disabled={!portal || busy}>
            {busy ? 'Preparing…' : done ? 'Download again' : 'Generate & download'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default GenerateReportDialog;
